import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import templateService from "../services/template-service";

// Initial state
const initialState = {
	templates: [],
	currentTemplate: null,
	// File upload state - moved from component local state
	uploadedFiles: {
		letterhead: null,
		logo: null,
		letterheadFile: null,
		logoFile: null,
	},
	loading: {
		templates: false,
		create: false,
		update: false,
		delete: false,
		upload: false,
	},
	filters: {
		search: "",
	},
	error: null,
};

// Async thunks
export const fetchTemplates = createAsyncThunk(
	"templates/fetchTemplates",
	async (params = {}, { rejectWithValue }) => {
		try {
			const response = await templateService.getTemplates(params);
			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export const fetchTemplate = createAsyncThunk(
	"templates/fetchTemplate",
	async (templateId, { rejectWithValue }) => {
		try {
			const response = await templateService.getTemplate(templateId);
			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export const createTemplate = createAsyncThunk(
	"templates/createTemplate",
	async (templateData, { rejectWithValue }) => {
		try {
			const response = await templateService.createTemplate(templateData);
			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export const updateTemplate = createAsyncThunk(
	"templates/updateTemplate",
	async ({ templateId, templateData }, { rejectWithValue }) => {
		try {
			const { id, created_by, updated_by, ...templateDataWithoutId } =
				templateData;
			const response = await templateService.updateTemplate(
				templateId,
				templateDataWithoutId,
			);
			return response;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export const deleteTemplate = createAsyncThunk(
	"templates/deleteTemplate",
	async (templateId, { rejectWithValue }) => {
		try {
			await templateService.deleteTemplate(templateId);
			return templateId;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

export const processLetterhead = createAsyncThunk(
	"templates/processLetterhead",
	async (file, { rejectWithValue }) => {
		try {
			const response = await templateService.processLetterhead(file);
			// Handle response structure: { data: { letterhead } } or { letterhead }
			return response.data?.letterhead || response.letterhead;
		} catch (error) {
			return rejectWithValue(error);
		}
	},
);

// Template slice
const templateSlice = createSlice({
	name: "templates",
	initialState,
	reducers: {
		setFilters: (state, action) => {
			state.filters = { ...state.filters, ...action.payload };
		},
		clearCurrentTemplate: (state) => {
			state.currentTemplate = null;
		},
		clearError: (state) => {
			state.error = null;
		},
		// File management actions
		setUploadedLetterhead: (state, action) => {
			state.uploadedFiles.letterhead = action.payload;
		},
		setUploadedLogo: (state, action) => {
			state.uploadedFiles.logo = action.payload;
		},
		setLetterheadFile: (state, action) => {
			state.uploadedFiles.letterheadFile = action.payload;
		},
		setLogoFile: (state, action) => {
			state.uploadedFiles.logoFile = action.payload;
		},
		clearUploadedFiles: (state) => {
			state.uploadedFiles = {
				letterhead: null,
				logo: null,
				letterheadFile: null,
				logoFile: null,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch templates
			.addCase(fetchTemplates.pending, (state) => {
				state.loading.templates = true;
				state.error = null;
			})
			.addCase(fetchTemplates.fulfilled, (state, action) => {
				state.loading.templates = false;
				// Backend returns: { data: { items: [...], count: n, total: n } }
				state.templates =
					action.payload.data?.items || action.payload.items || [];
				// Remove pagination since it's not being used
				// state.pagination = { ... };
			})
			.addCase(fetchTemplates.rejected, (state, action) => {
				state.loading.templates = false;
				state.error = action.payload;
			})

			// Fetch single template
			.addCase(fetchTemplate.pending, (state) => {
				state.loading.templates = true;
				state.error = null;
			})
			.addCase(fetchTemplate.fulfilled, (state, action) => {
				state.loading.templates = false;
				// Backend returns: { data: templateObject } or just templateObject
				state.currentTemplate = action.payload.data || action.payload;
			})
			.addCase(fetchTemplate.rejected, (state, action) => {
				state.loading.templates = false;
				state.error = action.payload;
			})

			// Create template
			.addCase(createTemplate.pending, (state) => {
				state.loading.create = true;
				state.error = null;
			})
			.addCase(createTemplate.fulfilled, (state, action) => {
				state.loading.create = false;
				// Backend returns: { data: templateObject } or just templateObject
				const newTemplate = action.payload.data || action.payload;
				state.templates.unshift(newTemplate);
			})
			.addCase(createTemplate.rejected, (state, action) => {
				state.loading.create = false;
				state.error = action.payload;
			})

			// Update template
			.addCase(updateTemplate.pending, (state) => {
				state.loading.update = true;
				state.error = null;
			})
			.addCase(updateTemplate.fulfilled, (state, action) => {
				state.loading.update = false;
				// Backend returns: { data: templateObject } or just templateObject
				const updatedTemplate = action.payload.data || action.payload;
				const index = state.templates.findIndex(
					(template) => template.id === updatedTemplate.id,
				);
				if (index !== -1) {
					state.templates[index] = updatedTemplate;
				}
				if (state.currentTemplate?.id === updatedTemplate.id) {
					state.currentTemplate = updatedTemplate;
				}
			})
			.addCase(updateTemplate.rejected, (state, action) => {
				state.loading.update = false;
				state.error = action.payload;
			})

			// Delete template
			.addCase(deleteTemplate.pending, (state) => {
				state.loading.delete = true;
				state.error = null;
			})
			.addCase(deleteTemplate.fulfilled, (state, action) => {
				state.loading.delete = false;
				state.templates = state.templates.filter(
					(template) => template.id !== action.payload,
				);
				if (state.currentTemplate?.id === action.payload) {
					state.currentTemplate = null;
				}
			})
			.addCase(deleteTemplate.rejected, (state, action) => {
				state.loading.delete = false;
				state.error = action.payload;
			})

			// Process letterhead (temporary)
			.addCase(processLetterhead.pending, (state) => {
				state.loading.upload = true;
				state.error = null;
			})
			.addCase(processLetterhead.fulfilled, (state, action) => {
				state.loading.upload = false;
				// Store the processed letterhead in Redux state with data URL
				const letterheadData = action.payload;
				if (letterheadData?.base64) {
					state.uploadedFiles.letterhead = {
						...letterheadData,
						preview: `data:image/png;base64,${letterheadData.base64}`,
					};
				}
			})
			.addCase(processLetterhead.rejected, (state, action) => {
				state.loading.upload = false;
				state.error = action.payload;
			});
	},
});

export const {
	setFilters,
	clearCurrentTemplate,
	clearError,
	setUploadedLetterhead,
	setUploadedLogo,
	setLetterheadFile,
	setLogoFile,
	clearUploadedFiles,
} = templateSlice.actions;

export default templateSlice.reducer;
